import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { ToastContainer } from '@azyntor-labs/nexus-ui';

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
  loading: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

let globalToast: ToastContextType | null = null;

export function NexusToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((type: ToastType, title: string, message?: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const duration = type === 'error' ? 5000 : 3000;
    setToasts((prev) => [...prev, { id, type, title, message, duration }]);
  }, []);

  const ctx: ToastContextType = {
    success: (title, message) => addToast('success', title, message),
    error: (title, message) => addToast('error', title, message),
    warning: (title, message) => addToast('warning', title, message),
    info: (title, message) => addToast('info', title, message),
    loading: (title, message) => addToast('loading', title, message),
  };

  globalToast = ctx;

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div
        role="region"
        aria-live="polite"
        aria-atomic="true"
        aria-label="Notifications"
        style={{ position: 'relative', zIndex: 10000 }}
      >
        <ToastContainer
          toasts={toasts}
          onClose={removeToast}
          position="top-right"
          maxToasts={5}
        />
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within NexusToastProvider');
  return ctx;
}

// Imperative API for toast notifications
// Supports: toast('msg'), toast.success('msg'), toast.error('msg')
type ToastOpts = string | Record<string, unknown>;

function toastFn(title: string, _opts?: ToastOpts) {
  globalToast?.info(title);
}

toastFn.success = (title: string, _opts?: ToastOpts) => globalToast?.success(title);
toastFn.error = (title: string, _opts?: ToastOpts) => globalToast?.error(title);
toastFn.warning = (title: string, _opts?: ToastOpts) => globalToast?.warning(title);
toastFn.info = (title: string, _opts?: ToastOpts) => globalToast?.info(title);
toastFn.loading = (title: string, _opts?: ToastOpts) => globalToast?.loading(title);

export const toast = toastFn;
